import { decamelizeKeys, type HumpsProcessor } from 'humps'
import { api, infiniteConfigSetting, WorkspacesAPITagType } from '@/api/api'
import getWorkspaceApiUrl from '@/api/utils/get-workspace-api-url'
import type { FetchCampaignKolListRequest } from '@/api/workspaces-api'
import type { ListResource } from '@/types/resource-type'
import {
  campaignKolSchema,
  type CampaignKol,
  type DeleteCampaignKolContractRequest,
  type LaborDeclarationRequest,
  type UpdateCampaignKolPaymentRequest,
} from '@/types/schema/campaign/campaign-kol'
import {
  campaignContractAcceptationSchema,
  type CampaignContractAcceptation,
  type FetchCampaignContractAcceptationRequest,
} from '@/types/schema/campaign/contract'
import {
  fetchCampaignContractTemplateListResponseSchema,
  type FetchCampaignContractTemplateListRequest,
  type FetchCampaignContractTemplateListResponse,
} from '@/types/schema/campaign/contract-template'
import { fileItemSchema } from '@/types/schema/file-item'
import {
  accountingInfoSchema,
  type AccountingInfo,
} from '@/types/schema/influencer/accounting-info-schema'
import { camelizeKeys, camelizeKeysHumpsProcessor } from '@/utils/humps'
import removeNil from '@/utils/remove-nil'

export const campaignApi = api.injectEndpoints({
  endpoints: (builder) => ({
    fetchCampaignContractTemplateList: builder.query<
      FetchCampaignContractTemplateListResponse,
      FetchCampaignContractTemplateListRequest
    >({
      query: ({ campaignId, page, campaign_kol_id }) => ({
        url: getWorkspaceApiUrl(
          `/campaigns/${campaignId}/campaign-contract-templates`,
        ),
        params: { page, campaign_kol_id },
      }),
      ...infiniteConfigSetting<
        FetchCampaignContractTemplateListRequest,
        FetchCampaignContractTemplateListResponse
      >(),
      extraOptions: {
        dataSchema: fetchCampaignContractTemplateListResponseSchema,
      },
    }),
    deleteCampaignKolContract: builder.mutation<
      void,
      DeleteCampaignKolContractRequest
    >({
      query: ({ campaignId, campaignKolId, campaignKolContractId }) => ({
        url: getWorkspaceApiUrl(
          `/campaigns/${campaignId}/campaign-kols/${campaignKolId}/campaign-kol-contracts/${campaignKolContractId}`,
        ),
        method: 'DELETE',
      }),
      invalidatesTags: [
        WorkspacesAPITagType.CampaignKolList,
        WorkspacesAPITagType.CampaignContractAcceptation,
      ],
    }),
    fetchCampaignContractAcceptation: builder.query<
      CampaignContractAcceptation,
      FetchCampaignContractAcceptationRequest
    >({
      query: ({ campaignId, campaignContractAcceptationId }) => ({
        url: getWorkspaceApiUrl(
          `/campaigns/${campaignId}/contract-acceptions/${campaignContractAcceptationId}/info`,
        ),
      }),
      providesTags: [WorkspacesAPITagType.CampaignContractAcceptation],
      extraOptions: {
        dataSchema: campaignContractAcceptationSchema,
      },
    }),
    updateCampaignKolPayment: builder.mutation<
      void,
      UpdateCampaignKolPaymentRequest
    >({
      query: ({ campaign_id, ...body }) => ({
        url: getWorkspaceApiUrl(
          `/campaigns/${campaign_id}/campaign-kols/payment`,
        ),
        method: 'POST',
        body: removeNil(body),
      }),
      invalidatesTags: [
        WorkspacesAPITagType.Campaign,
        WorkspacesAPITagType.CampaignKolList,
      ],
    }),
    deleteLaborDeclaration: builder.mutation<void, LaborDeclarationRequest>({
      query: ({ campaignId, campaignKolId }) => {
        return {
          url: getWorkspaceApiUrl(
            `/campaigns/${campaignId}/campaign-kols/${campaignKolId}/labor-declaration`,
          ),
          method: 'DELETE',
        }
      },
      invalidatesTags: [WorkspacesAPITagType.CampaignKolList],
    }),
    createLaborDeclaration: builder.mutation<void, LaborDeclarationRequest>({
      query: ({ campaignId, campaignKolId }) => {
        return {
          url: getWorkspaceApiUrl(
            `/campaigns/${campaignId}/campaign-kols/${campaignKolId}/labor-declaration`,
          ),
          method: 'POST',
        }
      },
      invalidatesTags: [WorkspacesAPITagType.CampaignKolList],
    }),
    fetchCampaignKolList: builder.query<
      ListResource<CampaignKol>,
      FetchCampaignKolListRequest
    >({
      query: ({ workspaceId, campaignId, ...params }) => {
        return {
          url: `/workspaces/${workspaceId}/campaigns/${campaignId}/campaign-kols`,
          params: decamelizeKeys(params),
        }
      },
      transformResponse: (response: object) => {
        const processor: HumpsProcessor = (key, convert, options) => {
          if (
            fileItemSchema
              .keyof()
              .options.includes(key as keyof typeof fileItemSchema.shape)
          ) {
            return key
          }

          if (
            campaignKolSchema
              .keyof()
              .options.includes(key as keyof typeof campaignKolSchema.shape)
          ) {
            return key
          }

          return accountingInfoSchema
            .keyof()
            .options.includes(key as keyof AccountingInfo)
            ? key
            : camelizeKeysHumpsProcessor(key, convert, options)
        }
        return camelizeKeys(
          response,
          processor,
        ) as unknown as ListResource<CampaignKol>
      },
      providesTags: [WorkspacesAPITagType.CampaignKolList],
    }),
  }),
  overrideExisting: false,
})

export const useDeleteCampaignKolContractMutation =
  campaignApi.endpoints.deleteCampaignKolContract.useMutation
export const useFetchCampaignContractAcceptationQuery =
  campaignApi.endpoints.fetchCampaignContractAcceptation.useQuery
export const useFetchCampaignContractTemplateListQuery =
  campaignApi.endpoints.fetchCampaignContractTemplateList.useQuery
export const useUpdateCampaignKolPaymentMutation =
  campaignApi.endpoints.updateCampaignKolPayment.useMutation
export const useDeleteLaborDeclarationMutation =
  campaignApi.endpoints.deleteLaborDeclaration.useMutation
export const useCreateLaborDeclarationMutation =
  campaignApi.endpoints.createLaborDeclaration.useMutation
export const useFetchCampaignKolListQuery =
  campaignApi.endpoints.fetchCampaignKolList.useQuery
